import React, { useEffect } from 'react';
import {
  Content,
  fetchContentListItems,
  fetchContentComments,
  Meta,
  aggregateContentData,
  Comment,
  SurveyGroup,
  ContentAggregate,
  Cast,
  Series,
} from 'common/apiService';
import Layout from 'common/Layout/Layout';
import Head from 'next/head';
import { __t } from 'common/i18n';
import { GetServerSideProps } from 'next';
import ContentHeader from 'common/Components/SingleContent/ContentHeader';
import ContentComments from 'common/Components/SingleContent/ContentComments';
import ContentSurvey from 'common/Components/SingleContent/ContentSurvey';
import { getContentUrl, getFirstEpisode, sanitize, sanitizeHtml } from 'common/utils';
import { safeJsonLd } from 'common/utils/safeJsonLd';
import ContentBody from 'common/Components/SingleContent/ContentBody';
import useConditionalProps from 'common/utils/useConditionalProps';
import { useUser } from 'common/CacheProvider';
import xIPCountry from 'common/apiService/xIPCountry';
import Breadcrumb from 'common/Breadcrumb';
import Seo from 'common/Seo';

export default function SingleContentPage({
  aggregate,
  bundle,
  commentsInitialUrl,
  commentsInitialData,
  surveyGroup,
  serverTime,
}: // params,
SingleContentPageProps) {
  const { video_content: content, series, cast } = aggregate;
  const { authorized } = useUser();

  const fetchProps = useConditionalProps((s) => s.fetch);
  const setPublicProps = useConditionalProps((s) => s.setPublicProps);

  const seasons = series?.seasons;
  const contentId = content.short_id;
  const contentLongId = content.id;
  const seriesId = content.series?.id;

  const firstEpisode = getFirstEpisode({ type: content.type, seasons, contentId });
  const firstEpisodeId = firstEpisode?.short_id;

  const { conditional_flag, flag } = content || firstEpisode;
  const flags = [conditional_flag, flag].join(', ');
  const { ticket_info } = content;
  useEffect(() => {
    setPublicProps({
      contentId,
      seriesId,
      firstEpisodeId,
      flags,
      ticket_info,
      serverTime,
      contentLongId,
    });
    if (authorized) {
      fetchProps();
    }
  }, [contentId, contentLongId, authorized, setPublicProps, seriesId, firstEpisodeId, flags, ticket_info, serverTime, fetchProps]);

  const isSeries = content.type === 'series';
  const contentBreadcrumbs = [
    { name: __t('common.home'), url: '/' },
    {
      name: isSeries ? __t('content.type.tv_shows') : __t('content.type.movies'),
      url: isSeries ? '/contents?types=series' : '/contents?types=single_video',
    },
    { name: content.page_title || content.title || content.slug, url: `/contents/${content.short_id}/${content.slug}` },
  ];

  return (
    <>
      <DocumentHead props={content} cast={cast} series={series} />
      <Breadcrumb items={contentBreadcrumbs} />
      <ContentHeader content={content} cast={aggregate.cast} />
      {surveyGroup && <ContentSurvey surveyGroup={surveyGroup} contentId={content.id} />}
      <Layout disableTopGutter>
        <ContentBody {...aggregate} content={content} seasons={seasons} bundle={bundle} />
        <ContentComments initialUrl={commentsInitialUrl} initialData={commentsInitialData} contentId={content?.id} />
      </Layout>
    </>
  );
}

const DocumentHead = ({ props, cast, series }: { props: Content; cast: Cast[]; series: Series }) => {
  const { short_id, slug, summary, type, cover_image, original_name, year, rate, rates } = props;
  const { seasons } = series ?? {};
  const { full } = sanitize({ raw: summary || '', textOnly: true });
  const description = sanitizeHtml(full) + '...';

  const actors = cast?.find((c) => c.role === 'بازیگر')?.artists.map((artist) => ({ '@type': 'Person', name: artist })) || [];
  const director = cast?.find((c) => c.role === 'کارگردان')?.artists[0] || '';

  const seasonSchema =
    seasons?.map((season) => ({
      '@type': 'TVSeason',
      name: season.title,
      seasonNumber: season.season,
      numberOfEpisodes: season.episodes?.length || 0,
    })) || [];

  const episodeSchema =
    seasons?.flatMap(
      (season) => season.episodes?.map((episode) => ({
        '@type': 'TVEpisode',
        episodeNumber: episode.episode,
        name: episode.title,
        partOfSeason: { '@type': 'TVSeason', seasonNumber: season.season },
        duration: episode.duration,
      })) || [],
    ) || [];

  const aggregateRating =
    rate && rates
      ? {
        '@type': 'AggregateRating',
        bestRating: '10',
        ratingCount: rates.toString(),
        ratingValue: (rate / 10).toString(),
      }
      : undefined;

  const schemaObject = {
    '@context': 'https://schema.org',
    '@type': type === 'single_video' || type === 'video_content_list' ? 'Movie' : 'TVSeries',
    name: `${getPageTitle(props)} (${sanitizeHtml(original_name)}) | ${__t('static_siteName')}`,
    ...(original_name && { alternateName: original_name }),
    ...(cover_image?.path && { image: cover_image.path }),
    ...(description && { description }),
    ...(year && { copyrightYear: year.toString() }),
    ...(director && { director: { '@type': 'Person', name: director } }),
    ...(actors.length > 0 && { actor: actors }),
    ...(seasonSchema.length > 0 && { containsSeason: seasonSchema }),
    ...(episodeSchema.length > 0 && { episode: episodeSchema }),
    ...(aggregateRating && { aggregateRating }),
  };

  return (
    <>
      <Seo
        title={`${getPageTitle(props)} | ${__t('static_siteName')}`}
        description={description || undefined}
        path={`/contents/${short_id}/${slug}`}
        image={cover_image?.path ? `${cover_image.path}?w=300` : undefined}
        type={type === 'single_video' || type === 'video_content_list' ? 'video.movie' : 'video.tv_show'}
        robots="index, follow"
      />
      <Head>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(schemaObject) }} />
      </Head>
    </>
  );
};

function getPageTitle({ page_title, type, title, slug }: Content) {
  if (type === 'episode') return title || slug;
  return page_title || title || slug;
}

export const getServerSideProps: GetServerSideProps<
  SingleContentPageProps,
  {
    id: string;
    slug: string[];
  }
> = async ({ params, req, res }) => {
  xIPCountry({ req, res });

  const { id: shortId } = params ?? {};
  if (!shortId) return { notFound: true };

  try {
    const { data: aggregate, meta } = await aggregateContentData(shortId);

    // if (aggregate.video_content && aggregate.video_content.flag.includes('live_stream')) {
    //   return {
    //     redirect: {
    //       permanent: false,
    //       destination: '/live-stream',
    //     },
    //   };
    // }

    const { id, type, short_id, slug } = aggregate.video_content;

    // Shorts have no single page — send typed URLs (and bookmarks) to the shorts player.
    if (type === 'short') {
      return {
        redirect: {
          permanent: false,
          destination: getContentUrl({ short_id: short_id || shortId, slug, type }),
        },
      };
    }

    const serverTime = meta.server_date_time;

    const commentsInitialUrl = `/video-contents/${id}/comments`;
    const { data: comments, meta: commentsMeta } = await fetchContentComments({
      id,
      offset: 0,
      count: 10,
    });
    const props: SingleContentPageProps = {
      aggregate,
      commentsInitialUrl,
      commentsInitialData: { data: comments, meta: commentsMeta },
      serverTime,
      params,
    };

    if (type === 'video_content_list') {
      const { data: bundle } = await fetchContentListItems(id);
      props.bundle = bundle;
    }

    return { props };
  } catch (error) {
    if (error?.response?.status === 404) return { notFound: true };
    throw new Error(error);
  }
};

type SingleContentPageProps = {
  aggregate: ContentAggregate;
  bundle?: Content[];
  commentsInitialUrl: string;
  commentsInitialData: { data: Comment[]; meta: Meta };
  surveyGroup?: SurveyGroup;
  serverTime: string | undefined;
  params?: { id: string; slug: string[] };
};
